import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Box, Card, CardContent, Grid, Paper, Chip } from '@mui/material';
import GoogleDriveIntegrationCard from '../sections/@dashboard/integrations/GoogleDriveAuth';

// Define consistent image styling
const imageStyle = {
  width: '100px',
  height: '100px',
  objectFit: 'contain'
};

// Adjust the icon components to use the style
const VeracrossIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/veracross.png`} alt='Veracross' sx={imageStyle} />;
const OneDriveIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/onedrive.png`} alt='OneDrive' sx={imageStyle} />;
const DropboxIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/dropbox.svg`} alt='Dropbox' sx={imageStyle} />;
const BoxIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/box.png`} alt='Box' sx={imageStyle} />;
const HubspotIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/hubspot.png`} alt='Hubspot' sx={imageStyle} />;
const RavennaIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/ravenna.png`} alt='Ravenna' sx={imageStyle} />;
const BlackbaudIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/blackbaud.png`} alt='Blackbaud' sx={imageStyle} />;
const SalesforceIcon = () => <Box component="img" src={`${process.env.PUBLIC_URL}/assets/integrations/salesforce.png`} alt='Salesforce' sx={imageStyle} />;

export default function IntegrationsPage() {
  return (
    <>
      <Helmet>
        <title>Integrations | Ask Lilac</title>
      </Helmet>

      <Container maxWidth="lg">
        <Box sx={{ mb: 5 }}>
          <Typography variant="h2" align="center" gutterBottom>
            Integrations
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary">
            Connect your tools and automate your knowledge base
          </Typography>
        </Box>

        {/* Google Drive Integration Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom>
            Google Drive Integration
          </Typography>
          <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom color="primary">
              How It Works
            </Typography>
            <Typography variant="body1" paragraph>
              Our Google Drive integration automatically keeps your knowledge base up-to-date with your documents:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>Smart Monitoring:</strong> We watch the "Ask Lilac" folder in your Google Drive for changes
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Automatic Updates:</strong> When you add, modify, or delete files, we automatically process them
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Real-time Sync:</strong> All your documents are automatically indexed and kept up-to-date
              </Typography>

            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Supported Formats:</strong> PDF, Word (.doc/.docx), PowerPoint (.ppt/.pptx), Text files (.txt), Google Docs, Google Sheets, Google Slides
            </Typography>
          </Paper>
          <GoogleDriveIntegrationCard />
        </Box>

        {/* Premium Integrations Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h4" gutterBottom>
            Additional Integrations
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Unlock these powerful integrations by upgrading your plan
          </Typography>
          <Grid container spacing={4}>
            {[
              { icon: <VeracrossIcon />, name: 'Veracross', description: 'Student Information System' },
              { icon: <OneDriveIcon />, name: 'OneDrive', description: 'Microsoft Cloud Storage' },
              { icon: <DropboxIcon />, name: 'Dropbox', description: 'Cloud File Sharing' },
              { icon: <BoxIcon />, name: 'Box', description: 'Enterprise Content Management' },
              { icon: <HubspotIcon />, name: 'Hubspot', description: 'CRM & Marketing Platform' },
              { icon: <RavennaIcon />, name: 'Ravenna', description: 'School Application System' },
              { icon: <BlackbaudIcon />, name: 'Blackbaud', description: 'Education Management' },
              { icon: <SalesforceIcon />, name: 'Salesforce', description: 'Customer Relationship Management' },
            ].map((service) => (
              <Grid item xs={6} sm={4} md={3} key={service.name}>
                <Card 
                  sx={{ 
                    height: '220px', 
                    textAlign: 'center', 
                    padding: 2, 
                    backgroundColor: '#f5f5f5',
                    opacity: 0.6,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ width: '100px', height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center', mx: 'auto', mb: 2 }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {service.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {service.description}
                    </Typography>
                    <Chip 
                      label="Premium" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Upgrade CTA */}
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Paper elevation={1} sx={{ p: 4, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h5" gutterBottom>
              Need More Integrations?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Upgrade your plan to unlock all integrations and build a comprehensive knowledge base
            </Typography>
            <Chip 
              label="Contact Sales" 
              color="primary" 
              size="medium"
              sx={{ cursor: 'pointer', px: 2 }}
            />
          </Paper>
        </Box>
      </Container>
    </>
  );
}
